import {Injectable} from '@angular/core';
import {CcCookieService} from "./cc-cookie.service";

const MIGRATION_FLAG = '_cc_storage_migration_v4_';

// Static UI keys: old name (cookie or localStorage) → new localStorage name
const STATIC_UI_RENAMES: { [old: string]: string } = {
  'IS_THEME_DARK':                  '_IS_THEME_DARK_',
  'CLEARING_VIEW_SELECTED':         '_CLEARING_VIEW_SELECTED_',
  'CLEARING_ORDER_TRADEMODE':       '_CLEARING_ORDER_TRADEMODE_',
  'CLEARING_TRADE_AGGREGATES':      '_CLEARING_TRADE_AGGREGATES_',
  'CLEARING_STATE_FILTER_SELECTED': '_CLEARING_STATE_FILTER_SELECTED_',
  'TRADE_SELECTION_BASE':           '_TRADE_SELECTION_BASE_',
  'TRADE_SELECTION_INFO':           '_TRADE_SELECTION_INFO_',
  'TRADE_SELECTION_INFO_LOCK':      '_TRADE_SELECTION_INFO_LOCK_',
  'TRADE_CTRL_A_MODE':              '_TRADE_CTRL_A_MODE_',
  'CLEARING_SIDE_BAR_MAXIMIZED':    '_CLEARING_SIDE_BAR_MAXIMIZED_',
  'MENU_SIDE_BAR_MAXIMIZED':        '_MENU_SIDE_BAR_MAXIMIZED_',
  'ARCHIVE_RECAP_FAV_LIST':         '_ARCHIVE_RECAP_FAV_LIST_',
  'ETICKET_VIEW_SELECTED':          '_ETICKET_VIEW_SELECTED_',
  'BILLING_INVOICE_REQUEST':        '_BILLING_INVOICE_REQUEST_',
  'ORDER_AMENDMENT_REQUEST':        '_ORDER_AMENDMENT_REQUEST_',
  'PAGE_BEFORE_LOGOUT':             '_PAGE_BEFORE_LOGOUT_',
};

// Dynamic UI keys: old prefix → new prefix (suffix is dynamic: viewId, origin, etc.)
const DYNAMIC_PREFIX_RENAMES: { [oldPrefix: string]: string } = {
  'CLEARING_FILTERS_SORT_SELECTED_': '_CLEARING_FILTERS_SORT_SELECTED_',
  'CLEARING_VISIBLE_COLUMNS_':       '_CLEARING_VISIBLE_COLUMNS_',
  'CLEARING_TABLE_MAXIMIZED_':       '_CLEARING_TABLE_MAXIMIZED_',
  'TRADE_SELECTION_FILTER_SELECTED_':'_TRADE_SELECTION_FILTER_SELECTED_',
};

// Dynamic UI keys: old stored suffix (including the '_' separator) → new stored suffix
// Old format: <origin>_ARCHIVE_FORM_RECORD  →  New format: <origin>_ARCHIVE_FORM_RECORD_
const DYNAMIC_SUFFIX_RENAMES: { old: string, new: string }[] = [
  { old: '_ARCHIVE_FORM_RECORD',          new: '_ARCHIVE_FORM_RECORD_' },
  { old: '_ARCHIVE_FORM_FORCEPREVIOUSDAY', new: '_ARCHIVE_FORM_FORCEPREVIOUSDAY_' },
  { old: '_ARCHIVE_FORM_USEPROFILES',     new: '_ARCHIVE_FORM_USEPROFILES_' },
  { old: '_ARCHIVE_PROFILE_TYPES',        new: '_ARCHIVE_PROFILE_TYPES_' },
];

// Security/identity keys: these used to be cookies (old lowercase names, then the
// uppercase '_USER_NAME_'-style names). They are PII and are no longer persisted to any
// client-side storage at all - see UserInfoStorageService's in-memory currentUserInfo
// cache, backed by GET /api/users/me. Both generations are purged outright below rather
// than migrated.
const SECURITY_COOKIES_TO_PURGE: string[] = [
  '_user_name_', '_USER_NAME_',
  '_first_name_', '_FIRST_NAME_',
  '_last_name_', '_LAST_NAME_',
  '_role_label_', '_ROLE_LABEL_',
  '_security_team_', '_SECURITY_TEAM_',
  '_user_groups_', '_USER_GROUPS_',
];

// The old '_session-id_' cookie is intentionally NOT migrated: '_CC_SESSION_ID_' is now the
// backend-owned HttpOnly session cookie name, and JS must never write to it (it would either
// be silently dropped by the browser, or - if no HttpOnly cookie exists yet for that name -
// resurrect a stale session id as a JS-writable, non-HttpOnly cookie).

// Lang key in localStorage/sessionStorage: old name → new name
const LOCAL_STORAGE_RENAMES: { old: string, new: string }[] = [
  { old: '_lang_', new: '_LANG_' },
];

@Injectable({
  providedIn: 'root',
})
export class CcCookieMigrationService {

  constructor(private ccCookieService: CcCookieService) {
  }

  migrate(): void {
    if (localStorage.getItem(MIGRATION_FLAG)) {
      return;
    }

    const allCookies = this.ccCookieService.getAll();

    // Static UI keys: read from old cookie or old localStorage → write to new localStorage
    for (const [oldName, newName] of Object.entries(STATIC_UI_RENAMES)) {
      if (allCookies[oldName] !== undefined) {
        if (!localStorage.getItem(newName)) {
          localStorage.setItem(newName, allCookies[oldName]);
        }
        this.ccCookieService.delete(oldName);
      }
      const storageValue = localStorage.getItem(oldName);
      if (storageValue) {
        if (!localStorage.getItem(newName)) {
          localStorage.setItem(newName, storageValue);
        }
        localStorage.removeItem(oldName);
      }
    }

    // Dynamic prefix keys: rename in localStorage and in cookies
    const allStorageKeys = Object.keys(localStorage);
    for (const storageKey of allStorageKeys) {
      for (const [oldPrefix, newPrefix] of Object.entries(DYNAMIC_PREFIX_RENAMES)) {
        if (storageKey.startsWith(oldPrefix)) {
          const newKey = newPrefix + storageKey.slice(oldPrefix.length);
          if (!localStorage.getItem(newKey)) {
            localStorage.setItem(newKey, localStorage.getItem(storageKey)!);
          }
          localStorage.removeItem(storageKey);
          break;
        }
      }
    }
    for (const [cookieKey, cookieValue] of Object.entries(allCookies)) {
      for (const [oldPrefix, newPrefix] of Object.entries(DYNAMIC_PREFIX_RENAMES)) {
        if (cookieKey.startsWith(oldPrefix)) {
          const newKey = newPrefix + cookieKey.slice(oldPrefix.length);
          if (!localStorage.getItem(newKey)) {
            localStorage.setItem(newKey, cookieValue);
          }
          this.ccCookieService.delete(cookieKey);
          break;
        }
      }
    }

    // Dynamic suffix keys: rename in localStorage
    // Old stored format: <origin>_ARCHIVE_FORM_RECORD (the '_' was a code-level separator)
    // New stored format: <origin>_ARCHIVE_FORM_RECORD_ (new value has trailing '_')
    for (const storageKey of Object.keys(localStorage)) {
      for (const rename of DYNAMIC_SUFFIX_RENAMES) {
        if (storageKey.endsWith(rename.old)) {
          const newKey = storageKey.slice(0, -rename.old.length) + rename.new;
          if (!localStorage.getItem(newKey)) {
            localStorage.setItem(newKey, localStorage.getItem(storageKey)!);
          }
          localStorage.removeItem(storageKey);
          break;
        }
      }
    }
    // Also handle old cookies with suffix format (no leading '_' on the suffix value)
    for (const [cookieKey, cookieValue] of Object.entries(allCookies)) {
      for (const rename of DYNAMIC_SUFFIX_RENAMES) {
        const oldCookieSuffix = rename.old; // stored format already includes separator '_'
        if (cookieKey.endsWith(oldCookieSuffix.slice(1))) { // strip leading '_' → old cookie had no leading '_'
          const newKey = cookieKey.slice(0, -(oldCookieSuffix.length - 1)) + rename.new;
          if (!localStorage.getItem(newKey)) {
            localStorage.setItem(newKey, cookieValue);
          }
          this.ccCookieService.delete(cookieKey);
          break;
        }
      }
    }

    // Security/identity cookies: purge outright (see SECURITY_COOKIES_TO_PURGE comment).
    for (const name of SECURITY_COOKIES_TO_PURGE) {
      this.ccCookieService.delete(name);
    }
    // Drop the legacy session cookie outright - see comment above, it must not be migrated.
    this.ccCookieService.delete('_session-id_');

    // Auth token and lang: rename in localStorage and sessionStorage
    for (const rename of LOCAL_STORAGE_RENAMES) {
      const value = localStorage.getItem(rename.old);
      if (value) {
        if (!localStorage.getItem(rename.new)) {
          localStorage.setItem(rename.new, value);
        }
        localStorage.removeItem(rename.old);
      }
      const sessionValue = sessionStorage.getItem(rename.old);
      if (sessionValue) {
        if (!sessionStorage.getItem(rename.new)) {
          sessionStorage.setItem(rename.new, sessionValue);
        }
        sessionStorage.removeItem(rename.old);
      }
    }

    localStorage.setItem(MIGRATION_FLAG, '1');
  }
}
